import { z } from "zod";
import {
  compareFullscriptPersonName,
  type FullscriptPersonName,
  isExactFullscriptPersonName,
} from "./patient-demographics";

/**
 * One row of `GET /clinic/search/patients`. Only the fields that pick the match are read.
 * `is_dependent` and the names are nullish because a row without them must still parse.
 */
const SearchPatientRowSchema = z.object({
  id: z.string(),
  email: z.string().nullish(),
  first_name: z.string().nullish(),
  last_name: z.string().nullish(),
  is_dependent: z.boolean().nullish(),
});

const SearchPatientRowsSchema = z.array(z.unknown());

/** Reads only the email of a row that failed the full parse. */
const RowEmailSchema = z.object({ email: z.string().nullish() });

/**
 * Most name candidates the caller reads to compare dates of birth. A household is a few people;
 * more candidates than this is not a case to guess in.
 */
const MAX_NAME_CANDIDATES = 4;

/** Reads only the date of birth of a `GET /clinic/patients/{id}` patient. */
const PatientBirthDateSchema = z.object({ date_of_birth: z.string().nullish() });

type SearchPatientRow = z.infer<typeof SearchPatientRowSchema>;

/** The person the search is for. `Member` satisfies this shape. */
export interface SearchPatientPerson extends FullscriptPersonName {
  /** FHIR `Patient.birthDate`. A partial date (yyyy, yyyy-mm) counts as missing. */
  dateOfBirth?: string;
}

/** The result of the email match on one page of search rows. */
type SearchPatientMatch =
  /**
   * `isSoleEmailRow`: this row is the only row with the email, and it is not a dependent. Only
   * then may the link go ahead without a date of birth (see `decideBirthDateLink`).
   */
  | { kind: "match"; index: number; isSoleEmailRow: boolean; isExactName: boolean }
  /**
   * Several rows have the person's name, for example "John Smith" and "John Smith Jr". The
   * caller reads each one and links the row whose date of birth equals the person's
   * (`pickCandidateByBirthDate`).
   */
  | { kind: "name-candidates"; indices: number[]; candidatePatientIds: string[] }
  | { kind: "no-match" }
  /** More than one row can be the person, or no row is clearly the person. */
  | { kind: "ambiguous"; candidatePatientIds: string[]; dependentCount: number }
  /** A malformed row can be the person, so no row is safe to pick. */
  | { kind: "invalid-rows"; issuePaths: string[] };

/** The match plus the issue paths of malformed rows that were skipped. */
type SearchPatientMatchResult = SearchPatientMatch & { skippedIssuePaths: string[] };

/**
 * Picks the search row that a member may link to by email.
 *
 * A Fullscript dependent (a child) can carry the guardian's email, so one email can match a
 * guardian row and one or more dependent rows. The email alone does not tell the guardian from
 * the child, so the name decides. `compareFullscriptPersonName` does the name compare.
 *
 * 1. Exactly one email row has the person's given and family name: that row wins, dependent or not.
 * 2. No email row has the name, no dependent row has the email, exactly one other row has it,
 *    and that row has the person's given name: that row wins. A last-name change (for example
 *    after a marriage) must not break the link. A child does not match a parent row, because the
 *    given names differ. A nickname also gives no match. No link is safer than a wrong link.
 * 3. More than one email row has the name (the compare forgives a suffix and extra given names,
 *    so a parent and a child can both match): the rows are candidates, and the date of birth
 *    decides. More than four candidates is ambiguous.
 * 4. Any other case is ambiguous. Linking the wrong row points one person's chart at another
 *    person's Fullscript patient, so the caller gets no match.
 *
 * The search response has no date of birth, and a parent and a child can have the same name. So a
 * match is a candidate only: the caller reads the patient and applies `decideBirthDateLink`.
 *
 * Each row is parsed on its own. A malformed row is skipped only when its email is readable and
 * differs from the target, because then it cannot be the person. Any other malformed row gives
 * `invalid-rows`. Skipping a row that has the email could turn an ambiguous page into a match.
 *
 * @returns The index into `rows` of the match, or the reason there is none, plus the issue paths
 *   of the skipped rows.
 */
export function matchSearchPatientRow({
  rows,
  email,
  person,
}: {
  rows: unknown;
  email: string;
  person: SearchPatientPerson;
}): SearchPatientMatchResult {
  const parsedRows = SearchPatientRowsSchema.safeParse(rows);
  if (!parsedRows.success) return { kind: "invalid-rows", issuePaths: [""], skippedIssuePaths: [] };

  const target = email.toLowerCase();
  const valid: { row: SearchPatientRow; index: number }[] = [];
  const skippedIssuePaths: string[] = [];
  const blockingIssuePaths: string[] = [];
  parsedRows.data.forEach((raw, index) => {
    const parsed = SearchPatientRowSchema.safeParse(raw);
    if (parsed.success) {
      valid.push({ row: parsed.data, index });
      return;
    }
    const paths = parsed.error.issues.map((issue) => [index, ...issue.path].join("."));
    if (isOtherEmailRow(raw, target)) skippedIssuePaths.push(...paths);
    else blockingIssuePaths.push(...paths);
  });
  if (blockingIssuePaths.length) {
    return { kind: "invalid-rows", issuePaths: blockingIssuePaths, skippedIssuePaths };
  }

  const match = matchValidRows({ rows: valid, target, person });
  return { ...match, skippedIssuePaths };
}

/** Why the caller may or may not link the matched row. */
type BirthDateLinkReason =
  | "dob-match"
  | "dob-mismatch"
  /** The member has no full date of birth, and the sole row has exactly the member's name. */
  | "sole-row-without-dob"
  /** Only the member has a full date of birth, and the sole row has exactly the member's name. */
  | "sole-row-exact-name"
  /** A date of birth is missing, and nothing else proves the row is the person. */
  | "missing-dob";

/**
 * Decides whether a name-matched search row may be linked, from its date of birth.
 *
 * - Both dates of birth exist: link only when they are equal.
 * - Either side has no full date of birth: link only the sole non-dependent email row
 *   (`isSoleEmailRow`), and only when its name is exactly the member's (`isExactName`). The name
 *   compare forgives a suffix, so without this a son "John Smith Jr" links to his father's row
 *   "John Smith" when one of the two has no date of birth.
 * - Any other case is no link.
 *
 * @param fullscriptPatient The patient as `GET /clinic/patients/{id}` returned it.
 */
export function decideBirthDateLink({
  fullscriptPatient,
  person,
  isSoleEmailRow,
  isExactName,
}: {
  fullscriptPatient: unknown;
  person: SearchPatientPerson;
  isSoleEmailRow: boolean;
  isExactName: boolean;
}): { shouldLink: boolean; reason: BirthDateLinkReason } {
  const fullscriptBirthDate = readFullscriptBirthDate(fullscriptPatient);
  const memberBirthDate = toFullDate(person.dateOfBirth);

  if (fullscriptBirthDate && memberBirthDate) {
    const isSame = fullscriptBirthDate === memberBirthDate;
    return { shouldLink: isSame, reason: isSame ? "dob-match" : "dob-mismatch" };
  }
  if (!isSoleEmailRow || !isExactName) return { shouldLink: false, reason: "missing-dob" };
  return {
    shouldLink: true,
    reason: memberBirthDate ? "sole-row-exact-name" : "sole-row-without-dob",
  };
}

/**
 * Picks the one name candidate whose date of birth equals the person's. No full member date of
 * birth, no candidate with that date, or more than one such candidate gives no pick.
 *
 * @param candidates Each candidate's row index and the patient `GET /clinic/patients/{id}` returned.
 */
export function pickCandidateByBirthDate({
  candidates,
  person,
}: {
  candidates: { index: number; fullscriptPatient: unknown }[];
  person: SearchPatientPerson;
}): { index: number } | { reason: "missing-dob" | "no-dob-match" | "several-dob-matches" } {
  const memberBirthDate = toFullDate(person.dateOfBirth);
  if (!memberBirthDate) return { reason: "missing-dob" };
  const sameBirthDate = candidates.filter(
    ({ fullscriptPatient }) => readFullscriptBirthDate(fullscriptPatient) === memberBirthDate,
  );
  if (sameBirthDate.length === 1) return { index: sameBirthDate[0].index };
  return { reason: sameBirthDate.length ? "several-dob-matches" : "no-dob-match" };
}

// ---- helpers ----

/** Applies the name rules to the rows that parsed. `target` is the lowercased email. */
function matchValidRows({
  rows,
  target,
  person,
}: {
  rows: { row: SearchPatientRow; index: number }[];
  target: string;
  person: SearchPatientPerson;
}): SearchPatientMatch {
  const emailMatches = rows.filter(({ row }) => row.email?.toLowerCase() === target);
  if (!emailMatches.length) return { kind: "no-match" };

  const dependentCount = emailMatches.filter(({ row }) => row.is_dependent === true).length;
  const isSoleEmailRow = emailMatches.length === 1 && !dependentCount;

  const compared = emailMatches.map(({ row, index }) => ({
    index,
    ...compareFullscriptPersonName({ member: person, fullscriptPatient: row }),
  }));
  const nameMatches = compared.filter((name) => name.givenMatches && name.familyMatches);
  if (nameMatches.length === 1) {
    const { index } = nameMatches[0];
    const isExactName = isExactFullscriptPersonName({
      member: person,
      fullscriptPatient: rows.find((row) => row.index === index)?.row,
    });
    return { kind: "match", index, isSoleEmailRow, isExactName };
  }

  // rule 3: a parent and a child can both pass the forgiving compare - the date of birth decides
  if (nameMatches.length > 1 && nameMatches.length <= MAX_NAME_CANDIDATES) {
    const indices = nameMatches.map(({ index }) => index);
    return {
      kind: "name-candidates",
      indices,
      candidatePatientIds: emailMatches
        .filter(({ index }) => indices.includes(index))
        .map(({ row }) => row.id),
    };
  }

  // rule 2: the sole non-dependent row is a candidate through a family-name change. It links only
  // when both dates of birth exist and match; decideBirthDateLink refuses it if either is missing
  if (isSoleEmailRow && compared[0].givenMatches) {
    return { kind: "match", index: compared[0].index, isSoleEmailRow, isExactName: false };
  }

  return {
    kind: "ambiguous",
    candidatePatientIds: emailMatches.map(({ row }) => row.id),
    dependentCount,
  };
}

/** True when a malformed row has a readable email that is not the target email. */
function isOtherEmailRow(raw: unknown, target: string): boolean {
  const parsed = RowEmailSchema.safeParse(raw);
  if (!parsed.success) return false;
  // a row w/o email never matches the email filter, same as a valid row
  return parsed.data.email?.toLowerCase() !== target;
}

function readFullscriptBirthDate(fullscriptPatient: unknown): string | undefined {
  const parsed = PatientBirthDateSchema.safeParse(fullscriptPatient);
  return toFullDate(parsed.success ? parsed.data.date_of_birth : undefined);
}

// partial FHIR dates (yyyy, yyyy-mm) and malformed values count as missing
function toFullDate(value: string | null | undefined): string | undefined {
  return value && z.iso.date().safeParse(value).success ? value : undefined;
}
